import React, { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import Grid from '@config/Grid/Grid'
import styles from '../PageLayout.module.scss'
import {
  type AuthTilePage,
  getAuthTileImage,
  getLocalDateKey,
  getMsUntilNextLocalDay
} from './authTileImages'

type PageLayoutProps = {
  children: React.ReactNode
}

function PublicPageLayout({ children }: PageLayoutProps) {
  const [imageTileHeight, setImageTileHeight] = React.useState(0)
  const [dateKey, setDateKey] = React.useState(getLocalDateKey)
  const formTileContainerRef = React.useRef<HTMLDivElement>(null)
  const location = useLocation()

  const currentPage = location.pathname.split('/')[1]
  const currentPathLength = location.pathname.split('/').length

  const { t } = useTranslation()

  const authTilePage = React.useMemo<AuthTilePage | null>(() => {
    if (currentPage === 'login') {
      return 'login'
    }

    if (currentPage === 'register') {
      return 'register'
    }

    if (currentPage === 'reset-password' && currentPathLength === 2) {
      return 'reset-password-email'
    }

    if (currentPage === 'reset-password' && currentPathLength === 3) {
      return 'reset-password'
    }

    return null
  }, [currentPage, currentPathLength])

  const imageTileBackground = authTilePage
    ? getAuthTileImage(authTilePage, dateKey)
    : undefined

  const imageTileStyle: React.CSSProperties = {
    height: `${imageTileHeight}px`,
    ...(imageTileBackground
      ? { backgroundImage: `url(${imageTileBackground})` }
      : {})
  }

  useEffect(() => {
    const formTileContainer = formTileContainerRef.current

    if (!formTileContainer) {
      return undefined
    }

    const updateImageTileHeight = () => {
      setImageTileHeight(formTileContainer.getBoundingClientRect().height)
    }

    updateImageTileHeight()

    const resizeObserver = new ResizeObserver(updateImageTileHeight)
    resizeObserver.observe(formTileContainer)

    return () => {
      resizeObserver.disconnect()
    }
  }, [location.pathname])

  useEffect(() => {
    const nextDayTimer = window.setTimeout(() => {
      setDateKey(getLocalDateKey())
    }, getMsUntilNextLocalDay() + 1000)

    return () => {
      window.clearTimeout(nextDayTimer)
    }
  }, [dateKey])

  return (
    <Grid>
      <div className={`start-3 col-4 align-center ${styles.publicPageLayout}`}>
        <div
          id="formTileContainer"
          ref={formTileContainerRef}
          className={`${styles.formTileContainer}`}
        >
          {children}
        </div>
      </div>
      <div
        className={`start-7 col-4 align-center ${styles.imageTileContainer}`}
        style={imageTileStyle}
      >
        {currentPage === 'register' && (
          <p className={`${styles.imageTileText} title-bold-medium`}>
            {t('AUTHORIZATION.REGISTER.IMAGE_TILE.FIRST_PART')}{' '}
            {t('AUTHORIZATION.REGISTER.IMAGE_TILE.SECOND_PART')}
            {t('AUTHORIZATION.REGISTER.IMAGE_TILE.THIRD_PART')}
            <mark className="mark-orange">
              {t('AUTHORIZATION.REGISTER.IMAGE_TILE.FOURTH_PART')}
            </mark>
          </p>
        )}
        {currentPage === 'login' && (
          <p className={`${styles.imageTileText} title-bold-medium`}>
            {t('AUTHORIZATION.LOGIN.IMAGE_TILE.FIRST_PART')}{' '}
            <mark className="mark-orange">
              {t('AUTHORIZATION.LOGIN.IMAGE_TILE.SECOND_PART')}
            </mark>
            {t('AUTHORIZATION.LOGIN.IMAGE_TILE.THIRD_PART')}
          </p>
        )}
        {currentPage === 'reset-password' && currentPathLength === 2 && (
          <p className={`${styles.imageTileText} title-bold-medium`}>
            {t('AUTHORIZATION.RESET_PASSWORD_EMAIL.IMAGE_TILE.FIRST_PART')}{' '}
            <mark className="mark-orange">
              {t('AUTHORIZATION.RESET_PASSWORD_EMAIL.IMAGE_TILE.SECOND_PART')}
            </mark>
            {t('AUTHORIZATION.RESET_PASSWORD_EMAIL.IMAGE_TILE.THIRD_PART')}
          </p>
        )}
        {currentPage === 'reset-password' && currentPathLength === 3 && (
          <p className={`${styles.imageTileText} title-bold-medium`}>
            {t('AUTHORIZATION.RESET_PASSWORD.IMAGE_TILE.FIRST_PART')}{' '}
            <mark className="mark-orange">
              {t('AUTHORIZATION.RESET_PASSWORD.IMAGE_TILE.SECOND_PART')}
            </mark>
            {t('AUTHORIZATION.RESET_PASSWORD.IMAGE_TILE.THIRD_PART')}
          </p>
        )}
      </div>
    </Grid>
  )
}

export default PublicPageLayout
